/**
 * Per-variant "read" of a launcher message. A variant is read once, while its bubble was visible,
 * the visitor clicked it (body or quick reply), opened the panel, or dismissed it with X. A read
 * variant stays hidden until its `contentHash` changes (an operator edit). An X dismiss may also
 * lapse after the slot's `reappearAfterDays`; a click or an open never does.
 *
 * State lives in instance storage (memory for `session="isolated"` / preview) under
 * `wexio:slot-read:<pk>:<slotId>` as `{ [variantId]: { contentHash, shownAt, readAt, dismissedAt } }`.
 * Storage may throw (private mode): every access is best-effort and never surfaces an error.
 */
import type { VisitorStorage } from "../visitor-storage";

const DAY_MS = 86_400_000;

export const slotReadKey = (pk: string, slotId: string) => `wexio:slot-read:${pk}:${slotId}`;

export interface VariantReadEntry {
  contentHash: string;
  shownAt?: string;
  /** A click or an opened panel: read until the content changes. */
  readAt?: string;
  /** An X / Esc dismiss: read until the content changes or `reappearAfterDays` passes. */
  dismissedAt?: string;
}

export type SlotReadState = Record<string, VariantReadEntry>;

export type ReadTrigger = "click" | "open" | "dismiss";

interface VariantRef {
  id: string;
  contentHash?: string;
}

const isoOrUndefined = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);

export function readSlotState(storage: VisitorStorage, pk: string, slotId: string): SlotReadState {
  try {
    const raw = storage.getItem(slotReadKey(pk, slotId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: SlotReadState = {};
    for (const [variantId, entry] of Object.entries(parsed)) {
      if (!entry || typeof entry !== "object") continue;
      const o = entry as Record<string, unknown>;
      if (typeof o.contentHash !== "string") continue;
      const shownAt = isoOrUndefined(o.shownAt);
      const readAt = isoOrUndefined(o.readAt);
      const dismissedAt = isoOrUndefined(o.dismissedAt);
      out[variantId] = {
        contentHash: o.contentHash,
        ...(shownAt ? { shownAt } : {}),
        ...(readAt ? { readAt } : {}),
        ...(dismissedAt ? { dismissedAt } : {}),
      };
    }
    return out;
  } catch {
    return {};
  }
}

function patchEntry(
  storage: VisitorStorage,
  pk: string,
  slotId: string,
  variant: VariantRef,
  patch: Partial<Omit<VariantReadEntry, "contentHash">>,
) {
  const state = readSlotState(storage, pk, slotId);
  const hash = variant.contentHash ?? "";
  const held = state[variant.id];
  // An edited variant (a new hash) is a new entry: nothing of the old one carries over.
  const base: VariantReadEntry = held && held.contentHash === hash ? held : { contentHash: hash };
  state[variant.id] = { ...base, ...patch };
  try {
    storage.setItem(slotReadKey(pk, slotId), JSON.stringify(state));
  } catch {
    // Private mode / blocked storage: the in-memory React state still holds for this mount.
  }
}

/** First time the bubble is displayed (kept as is afterwards). */
export function markVariantShown(
  storage: VisitorStorage,
  pk: string,
  slotId: string,
  variant: VariantRef,
  now: number,
) {
  const held = readSlotState(storage, pk, slotId)[variant.id];
  // Kept across plain re-displays; refreshed when the bubble comes back after a dismiss.
  const shownAfterDismiss = held?.shownAt && (!held.dismissedAt || held.shownAt > held.dismissedAt);
  if (held && held.contentHash === (variant.contentHash ?? "") && shownAfterDismiss) return;
  patchEntry(storage, pk, slotId, variant, { shownAt: new Date(now).toISOString() });
}

export function markVariantRead(
  storage: VisitorStorage,
  pk: string,
  slotId: string,
  variant: VariantRef,
  trigger: ReadTrigger,
  now: number,
) {
  const at = new Date(now).toISOString();
  patchEntry(
    storage,
    pk,
    slotId,
    variant,
    trigger === "dismiss" ? { dismissedAt: at } : { readAt: at },
  );
}

export function isVariantRead(
  state: SlotReadState,
  variant: VariantRef,
  reappearAfterDays: number | null,
  now: number,
): boolean {
  const entry = state[variant.id];
  if (!entry || entry.contentHash !== (variant.contentHash ?? "")) return false;
  if (entry.readAt) return true;
  if (!entry.dismissedAt) return false;
  if (reappearAfterDays === null) return true;
  const at = Date.parse(entry.dismissedAt);
  if (Number.isNaN(at)) return true;
  return now - at < reappearAfterDays * DAY_MS;
}

/** When a pure X dismiss stops hiding the variant (epoch ms), or `null` if nothing here ever lapses. */
export function dismissLapsesAt(
  state: SlotReadState,
  variant: VariantRef,
  reappearAfterDays: number | null,
): number | null {
  const entry = state[variant.id];
  if (!entry || entry.contentHash !== (variant.contentHash ?? "")) return null;
  if (entry.readAt || !entry.dismissedAt || reappearAfterDays === null) return null;
  const at = Date.parse(entry.dismissedAt);
  return Number.isNaN(at) ? null : at + reappearAfterDays * DAY_MS;
}
